import React, { FC, useState } from 'react'

import { ColorSchemePatchBody } from 'redux/api/Admin/Customization'

import { useTheme } from '@mui/material'
import { getContrastRatio } from '@mui/material/styles'
import { useTranslation } from 'language/translator'
import { useCustomer } from 'redux/hooks/Customer'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import ButtonField from 'components_new/molecules/ButtonField'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'

import ColorInput from 'components_new/molecules/ColorInput'
import NewSettingsItem from 'components_new/molecules/NewSettingsItem'

import LoadingSettings from 'components_new/organisms/pageLoaders/LoadingSettings'

import SettingsTemplate from 'components_new/templates/SettingsTemplate'

import EditLogotypeDialog from 'components_new/pages/settings/Customization/dialogs/EditLogotypeDialog'

import { getColorMode, getTheme } from 'themes'

import { useCustomization } from 'redux/hooks/Customization'
import { useCustomizationActions } from 'redux/actions/Admin/Customization'

const CustomizationPage: FC = () => {
  const theme = useTheme()
  const customer = useCustomer()
  const customization = useCustomization()

  const { t } = useTranslation()
  const { tryUpdateColorScheme } = useCustomizationActions()

  const updateCustomization = (params: ColorSchemePatchBody['data']) => {
    tryUpdateColorScheme({ data: params })
  }

  const allowWhiteLabelColors = customer?.allow_access.white_label_colors
  const allowWhiteLabelLogotype = customer?.allow_access.white_label_logotype

  const backgroundColors = customization.background_colors
  const logotype = customization.logotype
  const profileColors = customization.profile_colors
  const textColors = customization.text_colors

  const [editLogotype, setEditLogotype] = useState<boolean>(false)

  const MIN_TEXT_CONTRAST_RATIO = 4.5

  const getContrastWarnings = () => {
    const background = backgroundColors?.paper ?? theme.palette.background.paper

    return [
      { key: 'primary_color' as const, color: getPrimaryTextColor() },
      { key: 'secondary_color' as const, color: getSecondaryTextColor() }
    ]
      .map((item) => ({
        ...item,
        ratio: Math.round(getContrastRatio(item.color, background) * 10) / 10
      }))
      .filter((item) => item.ratio < MIN_TEXT_CONTRAST_RATIO)
  }

  const resetGraphColors = () => {
    updateCustomization({
      primary_color: null,
      secondary_color: null,
      tertiary_color: null,
      quaternary_color: null,
      quintary_color: null,
      senary_color: null,
      septenary_color: null,
      octonary_color: null,
      nonary_color: null,
      denary_color: null
    })
  }

  const resetAllColors = () => {
    updateCustomization({
      primary_color: null,
      secondary_color: null,
      tertiary_color: null,
      quaternary_color: null,
      quintary_color: null,
      senary_color: null,
      septenary_color: null,
      octonary_color: null,
      nonary_color: null,
      denary_color: null,
      text_primary_color: null,
      text_secondary_color: null,
      background_color: null,
      widget_background_color: null
    })
  }

  const getPrimaryColor = () => {
    if (profileColors?.primary) {
      return profileColors?.primary
    }

    if (backgroundColors?.paper) {
      return getTheme(getColorMode(backgroundColors?.paper)).palette.primary
        .main
    }

    return theme.palette.primary.main
  }

  const getPrimaryTextColor = () => {
    if (textColors?.primary) {
      return textColors?.primary
    }

    if (backgroundColors?.paper) {
      return getTheme(getColorMode(backgroundColors?.paper)).palette.text
        .primaryHex
    }

    return theme.palette.text.primaryHex
  }

  const getSecondaryTextColor = () => {
    if (textColors?.secondary) {
      return textColors?.secondary
    }

    if (backgroundColors?.paper) {
      return getTheme(getColorMode(backgroundColors?.paper)).palette.text
        .secondaryHex
    }

    return theme.palette.text.secondaryHex
  }

  const contrastWarnings = getContrastWarnings()

  if (!customer) return <LoadingSettings />

  return (
    <>
      <SettingsTemplate>
        {allowWhiteLabelLogotype ? (
          <NewSettingsItem
            actions={
              <Box sx={{ height: 72 }}>
                {logotype ? (
                  <Box
                    onClick={() => setEditLogotype(true)}
                    sx={{
                      position: 'relative',
                      height: '100%',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: 'action.hover',
                      '&:hover': {
                        bgcolor: 'action.selected'
                      },
                      width: 'fit-content',
                      p: 1,
                      borderRadius: 1
                    }}
                  >
                    <Icon
                      fontSize="small"
                      name="Edit"
                      sx={{ position: 'absolute', top: 4, right: 4 }}
                    />
                    <img
                      src={logotype}
                      style={{
                        height: '100%',
                        width: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                ) : (
                  <ButtonField
                    fullHeight={true}
                    onClick={() => setEditLogotype(true)}
                    sx={{ width: 144 }}
                  />
                )}
              </Box>
            }
            helperText={t('customization', 'logotype_helper_text')}
            title={t('customization', 'logotype')}
          />
        ) : null}

        {allowWhiteLabelColors ? (
          <NewSettingsItem
            actions={
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <ColorInput
                    hexCode={getPrimaryColor()}
                    isDefault={!profileColors?.primary}
                    title={t('customization', 'primary_color')}
                    onChange={(hexCode: string) =>
                      updateCustomization({ primary_color: hexCode })
                    }
                  />
                  <ColorInput
                    hexCode={
                      profileColors?.secondary ?? theme.palette.secondary.main
                    }
                    isDefault={!profileColors?.secondary}
                    title={t('customization', 'secondary_color')}
                    onChange={(hexCode: string) =>
                      updateCustomization({ secondary_color: hexCode })
                    }
                  />
                  <ColorInput
                    hexCode={
                      profileColors?.tertiary ?? theme.palette.tertiary.main
                    }
                    isDefault={!profileColors?.tertiary}
                    title={t('customization', 'tertiary_color')}
                    onChange={(hexCode: string) =>
                      updateCustomization({ tertiary_color: hexCode })
                    }
                  />
                  <ColorInput
                    hexCode={
                      profileColors?.quaternary ?? theme.palette.quaternary.main
                    }
                    isDefault={!profileColors?.quaternary}
                    title="#4"
                    onChange={(hexCode: string) =>
                      updateCustomization({ quaternary_color: hexCode })
                    }
                  />
                  <ColorInput
                    hexCode={
                      profileColors?.quintary ?? theme.palette.quintary.main
                    }
                    isDefault={!profileColors?.quintary}
                    title="#5"
                    onChange={(hexCode: string) =>
                      updateCustomization({ quintary_color: hexCode })
                    }
                  />
                  <ColorInput
                    hexCode={profileColors?.senary ?? theme.palette.senary.main}
                    isDefault={!profileColors?.senary}
                    title="#6"
                    onChange={(hexCode: string) =>
                      updateCustomization({ senary_color: hexCode })
                    }
                  />
                  <ColorInput
                    hexCode={
                      profileColors?.septenary ?? theme.palette.septenary.main
                    }
                    isDefault={!profileColors?.septenary}
                    title="#7"
                    onChange={(hexCode: string) =>
                      updateCustomization({ septenary_color: hexCode })
                    }
                  />
                  <ColorInput
                    hexCode={
                      profileColors?.octonary ?? theme.palette.octonary.main
                    }
                    isDefault={!profileColors?.octonary}
                    title="#8"
                    onChange={(hexCode: string) =>
                      updateCustomization({ octonary_color: hexCode })
                    }
                  />
                  <ColorInput
                    hexCode={profileColors?.nonary ?? theme.palette.nonary.main}
                    isDefault={!profileColors?.nonary}
                    title="#9"
                    onChange={(hexCode: string) =>
                      updateCustomization({ nonary_color: hexCode })
                    }
                  />
                  <ColorInput
                    hexCode={profileColors?.denary ?? theme.palette.denary.main}
                    isDefault={!profileColors?.denary}
                    title="#10"
                    onChange={(hexCode: string) =>
                      updateCustomization({ denary_color: hexCode })
                    }
                  />
                  <Button
                    disabled={
                      !Boolean(profileColors?.primary) &&
                      !Boolean(profileColors?.secondary) &&
                      !Boolean(profileColors?.tertiary) &&
                      !Boolean(profileColors?.quaternary) &&
                      !Boolean(profileColors?.quintary) &&
                      !Boolean(profileColors?.senary) &&
                      !Boolean(profileColors?.septenary) &&
                      !Boolean(profileColors?.octonary) &&
                      !Boolean(profileColors?.nonary) &&
                      !Boolean(profileColors?.denary)
                    }
                    onClick={resetGraphColors}
                    size="small"
                    variant="text"
                  >
                    {t('global', 'auto')}
                  </Button>
                </Box>
              </>
            }
            helperText={t('customization', 'colors_helper_text')}
            title={t('customization', 'profile_colors_title')}
          />
        ) : null}

        <NewSettingsItem
          actions={
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <ColorInput
                  hexCode={
                    backgroundColors?.default ?? theme.palette.white.main
                  }
                  isDefault={!backgroundColors?.default}
                  title={t('customization', 'dashboard_color')}
                  onChange={(hexCode: string) =>
                    updateCustomization({ background_color: hexCode })
                  }
                />
                <ColorInput
                  hexCode={backgroundColors?.paper ?? theme.palette.white.main}
                  isDefault={!backgroundColors?.paper}
                  title={t('customization', 'widget_color')}
                  onChange={(hexCode: string) =>
                    updateCustomization({ widget_background_color: hexCode })
                  }
                />
                <Button
                  disabled={
                    !Boolean(backgroundColors?.default) &&
                    !Boolean(backgroundColors?.paper)
                  }
                  onClick={() =>
                    updateCustomization({
                      background_color: null,
                      widget_background_color: null
                    })
                  }
                  size="small"
                  variant="text"
                >
                  {t('global', 'auto')}
                </Button>
              </Box>
            </>
          }
          helperText={t('customization', 'background_colors_helper_text')}
          title={t('customization', 'background_colors_title')}
        />

        <NewSettingsItem
          actions={
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <ColorInput
                  hexCode={getPrimaryTextColor()}
                  isDefault={!textColors?.primary}
                  title={t('customization', 'primary_color')}
                  onChange={(hexCode: string) =>
                    updateCustomization({ text_primary_color: hexCode })
                  }
                />
                <ColorInput
                  hexCode={getSecondaryTextColor()}
                  isDefault={!textColors?.secondary}
                  title={t('customization', 'secondary_color')}
                  onChange={(hexCode: string) =>
                    updateCustomization({ text_secondary_color: hexCode })
                  }
                />
                <Button
                  disabled={
                    !Boolean(textColors?.primary) &&
                    !Boolean(textColors?.secondary)
                  }
                  onClick={() =>
                    updateCustomization({
                      text_primary_color: null,
                      text_secondary_color: null
                    })
                  }
                  size="small"
                  variant="text"
                >
                  {t('global', 'auto')}
                </Button>
              </Box>
            </>
          }
          helperText={
            <>
              {t('customization', 'text_colors_helper_text')}
              {contrastWarnings.map((item) => (
                <Alert key={item.key} severity="warning" sx={{ mt: 1 }}>
                  {t('customization', 'contrast_warning', false, {
                    text: t('customization', item.key, true),
                    ratio: String(item.ratio)
                  })}
                </Alert>
              ))}
            </>
          }
          title={t('customization', 'text_colors_title')}
        />

        <NewSettingsItem
          actions={
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <ColorInput
                  disabled={true}
                  hexCode={theme.palette.success.main}
                  isDefault={true}
                  title={t('customization', 'positive_color')}
                />
                <ColorInput
                  disabled={true}
                  hexCode={theme.palette.warning.main}
                  isDefault={true}
                  title={t('customization', 'warning_color')}
                />
                <ColorInput
                  disabled={true}
                  hexCode={theme.palette.error.main}
                  isDefault={true}
                  title={t('customization', 'negative_color')}
                />
              </Box>
            </>
          }
          helperText={t('customization', 'status_colors_helper_text')}
          title={t('customization', 'status_colors_title')}
        />

        <Divider sx={{ my: 4 }} />

        <Button onClick={resetAllColors} variant="outlined">
          {t('global', 'reset_all')}
        </Button>
      </SettingsTemplate>

      <EditLogotypeDialog
        open={editLogotype}
        onClose={() => setEditLogotype(false)}
        onSave={(value) => updateCustomization({ logotype: value })}
        value={logotype}
      />
    </>
  )
}

export default CustomizationPage
